import { useEffect, useMemo, useState } from "react";
import { Trophy } from "lucide-react";
import Loading from "../../components/UI/Loading/Loading";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthProvider.jsx";
import { authService } from "../../services/api";
import "./index.css";
import { useNotification } from "../../components/Notifications/NotificationContext";
import { getVisibleBots } from "../../utils/bots";
import PushNotifications from "../../components/PushNotifications/PushNotifications";
import ManageBotsModal from "../../components/ManageBotsModal/ManageBotsModal";
import BotIcon from "../../components/Icons/BotIcon.jsx";
import ThemeCustomizer from "../../components/ThemeCustomizer/ThemeCustomizer";
import Sessions from "../../components/Sessions/Sessions";
import Premiacoes from "../../components/Premiacoes/Premiacoes";

export default function Profile() {
    const { showError, showSuccess, showInfo } = useNotification();
    const { user, isLoading, logout, loadUser } = useAuth();
    const navigate = useNavigate();

    const [username, setUsername] = useState("");
    const [editingName, setEditingName] = useState(false);
    const [loadingPage, setLoadingPage] = useState(false);
    const [hasPublicRevenue, setHasPublicRevenue] = useState(false);
    const [loadingPublicRevenue, setLoadingPublicRevenue] = useState(false);
    const [botsModalOpen, setBotsModalOpen] = useState(false);
    const [showPushHelp, setShowPushHelp] = useState(false);

    useEffect(() => {
        if (user) {
            setUsername(user.username);
            setHasPublicRevenue(Boolean(user.has_public_revenue));
        }
    }, [user]);

    const handleUsernameSubmit = async (e) => {
        e.preventDefault();
        if (!username || !user) return;
        setLoadingPage(true);
        try {
            await authService.change_username(username.trim());
            setEditingName(false);
            // Recarrega o usuário para o nome novo valer no resto do app (sidebar, ranking…).
            await loadUser();
            showSuccess("Nome de usuário atualizado com sucesso!");
        } catch (err) {
            console.warn('[Profile] change_username error:', err?.response?.data || err?.message);
            showError("Erro ao atualizar nome de usuário. " + (err?.response?.data?.error || err?.message || ''));
        } finally {
            setLoadingPage(false);
        }
    };

    const handleCancelEditName = () => {
        setUsername(user?.username || "");
        setEditingName(false);
    };

    const avatarInitial = (user?.username || user?.email || "?").trim().charAt(0).toUpperCase();

    const handleLogout = async () => {
        try {
            await logout();
            showInfo("Você saiu da conta com sucesso");
        } catch {
            showError("Erro ao sair da conta");
        }
    };

    const handlePublicRevenueToggle = async (checked) => {
        if (!user) return;
        setLoadingPublicRevenue(true);
        try {
            await authService.update_public_revenue({
                has_public_revenue: checked,
                public_name: username,
            });
            setHasPublicRevenue(checked);
            await loadUser();
            showSuccess("Preferência de ranking atualizada com sucesso!");
        } catch (err) {
            console.warn('[Profile] update_public_revenue error:', err?.response?.data || err?.message);
            showError("Erro ao atualizar preferência de ranking. " + (err?.response?.data?.error || err?.message || ''));
        } finally {
            setLoadingPublicRevenue(false);
        }
    };

    const visibleBotsCount = useMemo(() => {
        if (!Array.isArray(user?.bots)) return 0;
        return getVisibleBots(user.bots).length;
    }, [user]);

    if (isLoading) {
        return (
            <main className="page">
                <Loading text="Carregando perfil..." />
            </main>
        );
    }

    if (!user) {
        return null;
    }

    return (
        <>
            <main className="page profile-page">
                <header>
                    <div className="header-left">
                        <h1 className="title">Meu Perfil</h1>
                        <p className='text-gray-400'>Gerencie suas informações pessoais e configurações da conta</p>
                    </div>
                </header>

                <div className="profile-layout">
                    <div className="profile-col">

                {/* ===== Conta ===== */}
                <div className="stat-card-profile profile-hero">
                    <div className="profile-hero-top">
                        <div className="profile-avatar">{avatarInitial}</div>

                        <div className="profile-hero-id">
                            {editingName ? (
                                <form onSubmit={handleUsernameSubmit} className="profile-name-edit">
                                    <input
                                        className="input2"
                                        value={username}
                                        autoFocus
                                        onChange={(e) => setUsername(e.target.value)}
                                    />
                                    <button className="btn-primary btn-action" disabled={loadingPage} title="Salvar">
                                        <i className="fas fa-check" />
                                    </button>
                                    <button
                                        type="button"
                                        className="btn-secondary btn-action"
                                        onClick={handleCancelEditName}
                                        title="Cancelar"
                                    >
                                        <i className="fas fa-times" />
                                    </button>
                                </form>
                            ) : (
                                <div className="profile-name-row">
                                    <span className="profile-name">{user.username}</span>
                                    <button
                                        type="button"
                                        className="profile-name-edit-btn"
                                        onClick={() => setEditingName(true)}
                                        title="Editar nome de usuário"
                                    >
                                        <i className="fas fa-pen" />
                                    </button>
                                </div>
                            )}

                            <div className="profile-meta">
                                <i className="fas fa-envelope" />
                                <span>{user.email}</span>
                            </div>
                            <div className="profile-meta">
                                <i className="fas fa-calendar" />
                                <span>Membro desde {user.data_cadastro}</span>
                            </div>
                        </div>

                        {user.is_admin && (
                            <span className="profile-admin-badge">
                                <i className="fas fa-shield-halved" /> Admin
                            </span>
                        )}
                    </div>

                    <div className="profile-stats-row">
                        <div className="profile-stat-tile">
                            <span className="profile-stat-label">
                                <i className="fas fa-dollar-sign" /> Taxa
                            </span>
                            <span className="profile-stat-value">
                                R$ {user.fee}<small>/ transação</small>
                            </span>
                        </div>

                        <div className="profile-stat-tile">
                            <span className="profile-stat-label">
                                <BotIcon size={13} /> Bots
                            </span>
                            <span className="profile-stat-value">
                                {user.bots.length}<small>{visibleBotsCount} visíveis</small>
                            </span>
                        </div>
                    </div>

                    <button
                        type="button"
                        className="btn-primary bots-manage-button mt-4"
                        onClick={() => setBotsModalOpen(true)}
                    >
                        <i className="fas fa-sliders-h me-2" /> Gerenciar Bots
                    </button>
                </div>

                <ThemeCustomizer />

                    </div>

                    <div className="profile-col">

                {/* ===== Faturamento no ranking (acima das notificações) ===== */}
                <div className="ob-card mb-3">
                    <div className="p-4">
                        <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                                <Trophy size={14} strokeWidth={2.5} className="text-[#fb923c]" aria-hidden="true" />
                                <h3 className="font-semibold text-white text-sm">Mostrar meu faturamento no ranking</h3>
                            </div>
                            <label className="toggle-wrapper toggle-sm">
                                <input
                                    type="checkbox"
                                    checked={hasPublicRevenue}
                                    disabled={loadingPublicRevenue}
                                    onChange={(e) => handlePublicRevenueToggle(e.target.checked)}
                                />
                                <span className="toggle-slider" />
                            </label>
                        </div>
                        <p className="text-[11px] text-white/40">
                            Seu faturamento aparece no ranking público de vendedores.
                        </p>
                    </div>
                </div>

                {/* ===== Notificações (sem título) ===== */}
                <PushNotifications showHelp={showPushHelp} setShowHelp={setShowPushHelp} />

                {/* ===== Premiações ===== */}
                <Premiacoes />

                    </div>
                </div>

                {/* ===== Sessões ativas (largura total) ===== */}
                <Sessions />

                {/* ===== Ações (rodapé, largura total) ===== */}
                <div className="profile-footer">
                    <button
                        type="button"
                        className="push-help"
                        style={{ marginRight: "auto" }}
                        onClick={() => setShowPushHelp(true)}
                    >
                        <i className="fas fa-circle-question" />
                        Como ativar notificações?
                    </button>

                    {user.is_admin && (
                        <button
                            className="btn-primary wider"
                            onClick={() => navigate("/dashboard/admin/")}
                        >
                            <i className="fas fa-users" /> Admin
                        </button>
                    )}

                    <button onClick={handleLogout} className="btn-logout">
                        <i className="fas fa-sign-out-alt" /> Sair da Conta
                    </button>
                </div>
            </main>

            <ManageBotsModal
                user={user}
                open={botsModalOpen}
                onClose={() => setBotsModalOpen(false)}
                onSaved={loadUser}
            />
        </>
    );
}
